/**
 * A dropdown that cannot silently discard the value it was given.
 *
 * These fields were free text before, so a workspace may already hold something
 * that is not in the option list — a value from an older release, or a typo
 * somebody has been living with. A plain `<select>` would render no match,
 * report `""`, and write that emptiness back the first time anyone pressed Save.
 * So an unrecognised value is appended as its own option and marked, which both
 * preserves it and makes it visible as the odd one out.
 */
import * as React from 'react';
import { cn } from '../../lib/utils';

const SelectField = React.forwardRef(({ value, onChange, options = [], placeholder, className, ...props }, ref) => {
  const current = value === null || value === undefined ? '' : String(value);
  const known = options.some((o) => String(o.value) === current);
  const showUnknown = current !== '' && !known;

  return (
    <select
      ref={ref}
      value={current}
      onChange={(e) => onChange?.(e.target.value)}
      className={cn(
        'flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm',
        'ring-offset-background focus-visible:outline-none focus-visible:ring-2',
        'focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50',
        className,
      )}
      {...props}
    >
      {(placeholder || current === '') && (
        <option value="">{placeholder || 'Not set'}</option>
      )}
      {options.map((option) => (
        <option key={String(option.value)} value={String(option.value)}>
          {option.label}
        </option>
      ))}
      {showUnknown && (
        <option value={current}>{`${current} (current)`}</option>
      )}
    </select>
  );
});
SelectField.displayName = 'SelectField';

/**
 * Several of these fields are lists — the days a workspace is closed, for one.
 * Checkboxes rather than a multiple-select: a native multi-select needs a
 * modifier key nobody discovers, and silently deselects everything the first
 * time somebody clicks without it.
 */
export const CheckboxGroup = ({ value = [], onChange, options = [], columns = 'sm:grid-cols-3' }) => {
  const selected = new Set((Array.isArray(value) ? value : []).map(String));
  const toggle = (option) => {
    const next = new Set(selected);
    if (next.has(String(option))) next.delete(String(option));
    else next.add(String(option));
    // Emitted in the option order, not click order, so the stored list is
    // stable and two workspaces with the same days compare equal.
    onChange?.(options.map((o) => String(o.value)).filter((v) => next.has(v)));
  };

  return (
    <div className={cn('grid grid-cols-2 gap-x-4 gap-y-2', columns)}>
      {options.map((option) => (
        <label key={String(option.value)} className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            className="h-4 w-4 rounded border-input"
            checked={selected.has(String(option.value))}
            onChange={() => toggle(option.value)}
          />
          <span>{option.label}</span>
        </label>
      ))}
    </div>
  );
};

export { SelectField };
export default SelectField;
